"use client";

import type { CSSProperties } from "react";
import { DSFont } from "@/components/design-system/ArtistDesignSystem";
import type { GenArtModelDescriptor } from "./genArtCatalog";
import { useTranslation } from "@/i18n/ArtistI18n";

export interface GenArtModelSelectorRowProps {
	readonly models: readonly GenArtModelDescriptor[];
	readonly selectedModel: GenArtModelDescriptor | null;
	readonly onSelect: (model: GenArtModelDescriptor) => void;
}

const scrollerStyle: CSSProperties = {
	display: "flex",
	gap: "24px",
	overflowX: "auto",
	scrollSnapType: "x mandatory",
	scrollbarWidth: "none",
	padding: "0 20px",
	WebkitOverflowScrolling: "touch",
};

const buttonStyle: (isSelected: boolean) => CSSProperties = (isSelected) => ({
	scrollSnapAlign: "start",
	background: "transparent",
	border: 0,
	padding: "8px 0",
	font: DSFont.genArtHeavy(26),
	lineHeight: 1.1,
	color: isSelected
		? "var(--ds-on-media-primary)"
		: "color-mix(in srgb, var(--ds-on-media-primary) 70%, transparent)",
	whiteSpace: "nowrap",
	cursor: "pointer",
});

/**
 * GenArtModelSelectorRow — horizontal scrollable model name buttons.
 * Mirrors iOS `GenArtModelSelectorRow` (genArtHeavy 26px, 24px spacing,
 * 20px horizontal padding, selected = white, unselected = white 70%).
 */
export function GenArtModelSelectorRow({
	models,
	selectedModel,
	onSelect,
}: Readonly<GenArtModelSelectorRowProps>) {
	const { t } = useTranslation();
	return (
		<div role="tablist" aria-label={t("genart.models")} style={scrollerStyle}>
			{models.map((model) => {
				const isSelected = selectedModel?.id === model.id;
				return (
					<button
						key={model.id}
						type="button"
						role="tab"
						aria-selected={isSelected}
						style={buttonStyle(isSelected)}
						onClick={() => onSelect(model)}
					>
						{model.displayName}
					</button>
				);
			})}
		</div>
	);
}
